import { useEffect, useState, useCallback } from "react";
import { api, API, errText } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Star, CheckCircle2, XCircle, Video } from "lucide-react";

const CRITERIA = ["technique", "communication", "clarity"];

function ReviewPanel({ sub, onDone }) {
  const [scores, setScores] = useState({ technique: 0, communication: 0, clarity: 0 });
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);
  const { t, pick } = useLang();

  const submit = async (decision) => {
    if (CRITERIA.some((c) => !scores[c])) { toast.error("Score all criteria (1-5)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/assessor/submissions/${sub.id}/review`, { decision, scores, feedback });
      toast.success(decision === "approved" ? (data.certificate_id ? `Approved — certificate ${data.certificate_id} issued` : "Approved") : "Resubmission requested");
      onDone();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid={`review-panel-${sub.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-heading font-bold text-slate-900">{sub.user_name}</h2>
          <p className="text-sm text-slate-500">{pick(sub.track_title)} · {new Date(sub.created_at).toLocaleString()}</p>
        </div>
      </div>
      <video data-testid="submission-video-player" controls className="w-full rounded-xl bg-black max-h-72"
        src={`${API}/videos/${sub.id}/stream`} crossOrigin="use-credentials" />
      <div className="grid sm:grid-cols-3 gap-3">
        {CRITERIA.map((c) => (
          <div key={c} className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{c}</p>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} data-testid={`score-${c}-${n}`} onClick={() => setScores({ ...scores, [c]: n })}
                  className="p-1">
                  <Star size={22} className={n <= scores[c] ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <textarea data-testid="review-feedback-input" className="w-full rounded-xl border border-slate-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        rows={2} placeholder={t("feedback")} value={feedback} onChange={(e) => setFeedback(e.target.value)} />
      <div className="flex gap-2 flex-wrap">
        <button data-testid="approve-submission-btn" disabled={busy} onClick={() => submit("approved")}
          className="h-12 px-5 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2">
          <CheckCircle2 size={18} /> {t("approve")}
        </button>
        <button data-testid="reject-submission-btn" disabled={busy} onClick={() => submit("rejected")}
          className="h-12 px-5 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2">
          <XCircle size={18} /> {t("reject")}
        </button>
      </div>
    </div>
  );
}

export default function Assessor() {
  const [queue, setQueue] = useState(null);
  const [active, setActive] = useState(null);
  const { t, pick } = useLang();

  const load = useCallback(() => {
    api.get("/assessor/queue").then((r) => { setQueue(r.data); setActive(null); }).catch(() => setQueue([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!queue) return <p className="text-slate-500">Loading…</p>;

  return (
    <div className="space-y-4">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">{t("assessorConsole")}</h1>
      <p className="text-sm text-slate-500">{queue.length} pending video submission(s)</p>
      {queue.length === 0 && <p data-testid="empty-queue-msg" className="text-slate-400 bg-white rounded-2xl border border-slate-200 p-6 text-center">Queue is empty 🎉</p>}
      <div className="grid gap-3">
        {queue.map((s) => (
          active === s.id ? (
            <ReviewPanel key={s.id} sub={s} onDone={load} />
          ) : (
            <button key={s.id} data-testid={`queue-item-${s.id}`} onClick={() => setActive(s.id)}
              className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3 text-left hover:border-teal-300 transition-colors">
              <span className="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center shrink-0"><Video size={20} /></span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 truncate">{s.user_name}</p>
                <p className="text-sm text-slate-500 truncate">{pick(s.track_title)} · {new Date(s.created_at).toLocaleDateString()}</p>
              </div>
              <span className="text-xs font-semibold bg-amber-100 text-amber-800 px-3 py-1.5 rounded-full shrink-0">{t("pending")}</span>
            </button>
          )
        ))}
      </div>
    </div>
  );
}
